import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { Toaster } from "sonner";
import LandingPage from "@/pages/LandingPage";
import AuthPage from "@/pages/AuthPage";
import AuthCallback from "@/pages/AuthCallback";
import ProtectedRoute from "@/components/ProtectedRoute";
import "@/App.css";

// Everything below is only needed once a visitor goes past the homepage /
// auth screen, so it's code-split out of the initial bundle — landing-page
// and crawler requests no longer have to download the entire authenticated
// app (dashboard, budgeting, agreements, PDF export, etc.) up front.
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const ProjectView = lazy(() => import("@/pages/ProjectView"));
const UpgradePage = lazy(() => import("@/pages/UpgradePage"));
const PaymentSuccess = lazy(() => import("@/pages/PaymentSuccess"));
const AccountPage = lazy(() => import("@/pages/AccountPage"));
const SmsOptInPage = lazy(() => import("@/pages/SmsOptInPage"));
const ConfirmCallSheetPage = lazy(() => import("@/pages/ConfirmCallSheetPage"));
const ContactPage = lazy(() => import("@/pages/ContactPage"));
const PrivacyPolicyPage = lazy(() => import("@/pages/PrivacyPolicyPage"));
const TermsPage = lazy(() => import("@/pages/TermsPage"));
const CommunityPage = lazy(() => import("@/pages/CommunityPage"));
const VsSpellSlatePage = lazy(() => import("@/pages/VsSpellSlatePage"));
const SmsConsentPage = lazy(() => import("@/pages/SmsConsentPage"));
const FoundingPage = lazy(() => import("@/pages/FoundingPage"));
const ForgotPasswordPage = lazy(() => import("@/pages/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("@/pages/ResetPasswordPage"));
const ScriptBreakdownSoftwarePage = lazy(() => import("@/pages/ScriptBreakdownSoftwarePage"));
const FilmCallSheetSoftwarePage = lazy(() => import("@/pages/FilmCallSheetSoftwarePage"));
const FilmBudgetingSoftwarePage = lazy(() => import("@/pages/FilmBudgetingSoftwarePage"));
const ShotListSoftwarePage = lazy(() => import("@/pages/ShotListSoftwarePage"));
const IndieFilmProductionSoftwarePage = lazy(() => import("@/pages/IndieFilmProductionSoftwarePage"));
const StudioBinderAlternativePage = lazy(() => import("@/pages/StudioBinderAlternativePage"));
const AgreementsListPage = lazy(() => import("@/pages/AgreementsPages").then(m => ({ default: m.AgreementsListPage })));
const AgreementEditorPage = lazy(() => import("@/pages/AgreementsPages").then(m => ({ default: m.AgreementEditorPage })));
const SignAgreementPage = lazy(() => import("@/pages/SignAgreementPage"));
const AgreementsArchivePage = lazy(() => import("@/pages/AgreementsArchivePage"));

const RouteFallback = () => (
  <div className="min-h-screen bg-[#0D0D0D] flex items-center justify-center" data-testid="route-loading">
    <p className="text-[#555555] font-heading text-sm animate-pulse">Loading&hellip;</p>
  </div>
);

const TOASTER_OPTIONS = {
  style: {
    background: '#1A1A1A',
    color: '#F2F2F2',
    border: '1px solid rgba(255,255,255,0.08)',
    fontFamily: 'Manrope, sans-serif'
  }
};

function AppRouter() {
  const location = useLocation();

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/auth" element={<AuthPage />} />
        <Route path="/signup" element={<AuthPage />} />
        <Route path="/login" element={<AuthPage />} />
        <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/projects/:id" element={<ProtectedRoute><ProjectView /></ProtectedRoute>} />
        <Route path="/upgrade" element={<ProtectedRoute><UpgradePage /></ProtectedRoute>} />
        <Route path="/account" element={<ProtectedRoute><AccountPage /></ProtectedRoute>} />
        <Route path="/agreements" element={<ProtectedRoute><AgreementsListPage /></ProtectedRoute>} />
        <Route path="/agreements/archive" element={<ProtectedRoute><AgreementsArchivePage /></ProtectedRoute>} />
        <Route path="/agreements/:id" element={<ProtectedRoute><AgreementEditorPage /></ProtectedRoute>} />
        <Route path="/sign/:agreementId/:token" element={<SignAgreementPage />} />
        <Route path="/payment-success" element={<ProtectedRoute><PaymentSuccess /></ProtectedRoute>} />
        <Route path="/sms-optin" element={<SmsOptInPage />} />
        <Route path="/sms-opt-in" element={<SmsOptInPage />} />
        <Route path="/confirm/:projectId/:confId" element={<ConfirmCallSheetPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        <Route path="/privacy.html" element={<Navigate to="/privacy" replace />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/terms.html" element={<Navigate to="/terms" replace />} />
        <Route path="/community" element={<CommunityPage />} />
        <Route path="/vs-spell-slate" element={<VsSpellSlatePage />} />
        <Route path="/sms-consent" element={<SmsConsentPage />} />
        <Route path="/founding" element={<FoundingPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/script-breakdown-software" element={<ScriptBreakdownSoftwarePage />} />
        <Route path="/film-call-sheet-software" element={<FilmCallSheetSoftwarePage />} />
        <Route path="/film-budgeting-software" element={<FilmBudgetingSoftwarePage />} />
        <Route path="/shot-list-software" element={<ShotListSoftwarePage />} />
        <Route path="/indie-film-production-software" element={<IndieFilmProductionSoftwarePage />} />
        <Route path="/studiobinder-alternative" element={<StudioBinderAlternativePage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <div className="app-wrapper">
          <div className="grain-overlay" />
          <div className="vignette-overlay" />
          <AppRouter />
          <Toaster
            position="bottom-right"
            theme="dark"
            toastOptions={TOASTER_OPTIONS}
          />
        </div>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
